import { Grid } from '@sequencehq/core-components'

import { filterErrorsByField } from 'Automations/automationEditor/utils'
import type { ConditionPropertyEditorComponent } from 'Automations/nodeDefinitions/conditions'
import type { PhaseNameOperator } from 'Automations/nodeDefinitions/conditions/helpers/functionStrings/phase'
import {
  InputSelectControlField,
  TextInputField
} from 'Automations/nodeDefinitions/components'
import { SUB_OPERATOR_COLUMN_WIDTH } from '../conditionEditorLayout'
import {
  appendPhaseTestId,
  readPhaseEditorValue,
  usePhaseOperatorState,
  type PhaseEditorValue
} from './PhasePropertyEditor.helpers'
import { PhasePropertyEditorLayout } from './PhasePropertyEditorLayout'

export const NameOperatorEditor: ConditionPropertyEditorComponent = ({
  'data-testid': dataTestId,
  operator,
  value,
  operators,
  siblingConditions,
  onChange,
  validationErrors,
  focusOnInitialLoad,
  propertyInput,
  removeButton
}) => {
  const { operator: subOperator, name } = readPhaseEditorValue(value)
  const {
    operatorOptions,
    primaryOperatorDisabled,
    onPrimaryOperatorChange,
    subOperatorOptions,
    subOperatorDisabled
  } = usePhaseOperatorState({
    operator,
    value,
    operators,
    siblingConditions,
    onChange
  })

  const update = (next: Partial<PhaseEditorValue>) =>
    onChange({
      value: {
        operator: next.operator ?? subOperator,
        name: next.name ?? name ?? ''
      }
    })

  const showName = Boolean(subOperator && subOperator !== 'NOT_SET')

  return (
    <PhasePropertyEditorLayout
      propertyInput={propertyInput}
      removeButton={removeButton}
      operatorInput={
        <InputSelectControlField
          data-testid={appendPhaseTestId(dataTestId, 'operator')}
          value={operator}
          options={operatorOptions}
          placeholder="Select..."
          disabled={primaryOperatorDisabled}
          onChange={onPrimaryOperatorChange}
          validationErrors={validationErrors?.operator}
          initiallyOpen={focusOnInitialLoad}
        />
      }
      subRow={
        <Grid
          gridTemplateColumns={
            showName
              ? `${SUB_OPERATOR_COLUMN_WIDTH} minmax(0, 1fr)`
              : SUB_OPERATOR_COLUMN_WIDTH
          }
          gap="8px"
        >
          <InputSelectControlField
            data-testid={appendPhaseTestId(dataTestId, 'subOperator')}
            value={subOperator ?? ''}
            options={subOperatorOptions}
            placeholder="Select..."
            disabled={subOperatorDisabled}
            onChange={selected =>
              update({ operator: selected as PhaseNameOperator })
            }
            validationErrors={filterErrorsByField(
              validationErrors?.value,
              'operator'
            )}
          />
          {showName && (
            <TextInputField
              data-testid={appendPhaseTestId(dataTestId, 'name')}
              value={name ?? ''}
              placeholder="Enter name..."
              onChange={event => update({ name: event.target.value })}
              validationErrors={filterErrorsByField(
                validationErrors?.value,
                'name'
              )}
            />
          )}
        </Grid>
      }
    />
  )
}
